<script lang="ts">
export default {
  name: 'PromptListTable',
};
</script>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { DateTime } from 'luxon';
import { Pencil } from '@lucide/vue';
import Badge from '@/components/ui/Badge.vue';
import type { PromptListRow } from '@/types/ui/PromptManager';

// Search and the policy-type filter are rendered beside the page title, so the
// values arrive as props rather than being owned here.
const props = withDefaults(
  defineProps<{
    rows: PromptListRow[];
    loading?: boolean;
    searchText?: string;
    coverageFilter?: string | null;
  }>(),
  { loading: false, searchText: '', coverageFilter: '' },
);

const emit = defineEmits<{
  select: [promptKey: string, version: string];
}>();

type SortKey = 'name' | 'coverageTypes' | 'modelArn' | 'status' | 'versionCount' | 'lastModified' | 'modifiedBy';

const sortKey = ref<SortKey>('lastModified');
const sortAscending = ref(false);

const formatDate = (value: string) => {
  const parsed = DateTime.fromISO(value);
  return parsed.isValid ? parsed.toFormat('MMM d, yyyy') : '—';
};

const sortValue = (row: PromptListRow, key: SortKey): string | number => {
  switch (key) {
    case 'lastModified':
      return DateTime.fromISO(row.lastModified).toMillis() || 0;
    case 'versionCount':
      return row.versionCount;
    case 'coverageTypes':
      return row.coverageTypes.length;
    case 'modelArn':
      return row.modelName.toLowerCase();
    default:
      return String(row[key] ?? '').toLowerCase();
  }
};

const visibleRows = computed(() => {
  const search = props.searchText.trim().toLowerCase();

  const filtered = props.rows.filter((row) => {
    const matchesSearch = !search || row.name.toLowerCase().includes(search);
    const matchesCoverage = !props.coverageFilter || row.coverageTypes.includes(props.coverageFilter);
    return matchesSearch && matchesCoverage;
  });

  return [...filtered].sort((a, b) => {
    const left = sortValue(a, sortKey.value);
    const right = sortValue(b, sortKey.value);
    const result = typeof left === 'number' && typeof right === 'number' ? left - right : String(left).localeCompare(String(right));
    return sortAscending.value ? result : -result;
  });
});

const toggleSort = (key: SortKey) => {
  if (sortKey.value === key) {
    sortAscending.value = !sortAscending.value;
    return;
  }
  sortKey.value = key;
  sortAscending.value = key !== 'lastModified';
};

const ariaSort = (key: SortKey) => {
  if (sortKey.value !== key) return 'none';
  return sortAscending.value ? 'ascending' : 'descending';
};

// sorted/ascending/descending are what global.css keys the ▲▼ indicators off.
const headerClass = (column: { key: SortKey; class?: string }) => [
  column.class,
  sortKey.value === column.key ? ['sorted', sortAscending.value ? 'ascending' : 'descending'] : [],
];

const columns: Array<{ key: SortKey; label: string; class?: string }> = [
  { key: 'name', label: 'Prompt Name' },
  { key: 'coverageTypes', label: 'Coverage Types' },
  { key: 'modelArn', label: 'Model' },
  { key: 'status', label: 'Status' },
  { key: 'versionCount', label: 'Versions', class: 'text-right' },
  { key: 'lastModified', label: 'Last Modified' },
  { key: 'modifiedBy', label: 'Modified By' },
];
</script>

<template>
  <div>
    <div
      v-if="loading"
      class="py-8 text-center text-slate-500">
      Loading prompts...
    </div>

    <div
      v-else-if="rows.length === 0"
      class="py-8 text-center text-slate-500">
      No prompts found. Click "New Prompt" to create one.
    </div>

    <div
      v-else-if="visibleRows.length === 0"
      class="py-8 text-center text-slate-500">
      No prompts match the current search and filter.
    </div>


    <table
      v-else
      id="prompt-list"
      class="apptable sortable dense">
      <thead>
        <tr>
          <td
            v-for="column in columns"
            :key="column.key"
            class="text-left"
            :class="headerClass(column)"
            :aria-sort="ariaSort(column.key)"
            @click="toggleSort(column.key)">
            {{ column.label }}
          </td>
          <td class="nosort w-8"></td>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="row in visibleRows"
          :key="row.promptKey"
          class="group cursor-pointer hover:bg-gray-50"
          @click="emit('select', row.promptKey, row.representativeVersion)">
          <td class="text-left font-medium">{{ row.name }}</td>
          <td class="text-left">
            <div
              v-if="row.coverageTypes.length > 0"
              class="tooltip tooltip-right">
              <div class="tooltip-content">
                <ul class="space-y-0.5 px-1 py-0.5 text-left text-xs">
                  <li
                    v-for="name in row.coverageTypes"
                    :key="name">
                    {{ name }}
                  </li>
                </ul>
              </div>
              <span
                class="cursor-default underline decoration-dotted underline-offset-2"
                :aria-label="`${row.coverageTypes.length} coverage types: ${row.coverageTypes.join(', ')}`">
                {{ row.coverageTypes.length }}
              </span>
            </div>
            <span
              v-else
              class="text-slate-400"
              >—</span
            >
          </td>
          <td
            class="text-left"
            :title="row.modelArn">
            {{ row.modelName }}
          </td>
          <td class="text-left">
            <Badge :variant="row.status === 'Published' ? 'default' : 'secondary'">{{ row.status }}</Badge>
          </td>
          <td class="text-right">{{ row.versionCount }}</td>
          <td class="text-left">{{ formatDate(row.lastModified) }}</td>
          <td class="text-left">{{ row.modifiedBy }}</td>
          <td class="w-8 text-right">
            <!-- Affordance only: the whole row is the click target. -->
            <Pencil
              class="ml-auto h-3.5 w-3.5 text-slate-500 opacity-0 transition-opacity group-hover:opacity-60"
              aria-hidden="true" />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
